import { isFreeInput, mapFormElements } from "@common/controls/Form/DynamicForm/lib";
import { type FormFieldProps } from "@common/controls/Form/Field/component";
import { FormField, type OnChange } from "@common/controls/Form/component";
import Select from "@common/controls/Inputs/Select/component";
import { TextInput } from "@common/controls/Inputs/Text";
import { InlinePreloader } from "@common/controls/Preloader/component";
import { type GenericMap, type LabelValue } from "@common/declarations";
import { useI18n } from "@common/hooks/i18n";
import { useUpdated } from "@common/lib/hooks";
import { type I18n } from "@common/lib/i18n";
import { type Account } from "@common/modules/account/types";
import { usePrevious } from "@dnd-kit/utilities";
import { intersection, set } from "lodash";
import * as React from "react";
import { type IRecipientFields } from "../types";
import { type AddEditRecipientGetDynamicFields, type AddUpdateRecipientFormData, type DynamicField } from "./AddUpdateModal";

const getBankDetails = (values: GenericMap<any> | null, fields: DynamicField[]): GenericMap<any> =>
  values
    ? intersection(
        Object.keys(values).filter((x) => x !== "formType"),
        fields.map((x) => x.name)
      ).reduce((res, x) => {
        set(res, x, values[x]);
        return res;
      }, {})
    : {};

const getShouldReload = (
  fields: DynamicField[],
  previousFieldValues: GenericMap<any> | null | undefined,
  fieldValues: GenericMap<any> | null
): boolean => {
  if (!fields.length) return true;

  if (fieldValues) {
    if (!previousFieldValues) return false;

    return Object.keys(fieldValues).some((x) => {
      if (fieldValues[x] === previousFieldValues[x]) return false;

      return fields.find((y) => y.name === x)?.refreshRequirementsOnChange;
    });
  }

  return false;
};

const getAccountTypeOptions = (i18n: I18n): LabelValue<string>[] => [
  { label: i18n.bond, value: "Bond" },
  { label: i18n.cheque, value: "Cheque" },
  { label: i18n.savings, value: "Savings" },
  { label: i18n.transmission, value: "Transmission" }
];

type Props = {
  ref: React.RefObject<IRecipientFields | null>;
  beneficiaryId: string | undefined;
  getDynamicFields: AddEditRecipientGetDynamicFields;
  onChange: OnChange<AddUpdateRecipientFormData>;
  values: AddUpdateRecipientFormData;
};

export default (props: Props) => {
  const i18n = useI18n();

  const [fields, setFields] = React.useState<DynamicField[]>([]);
  const [isLoading, setIsLoading] = React.useState<boolean>();
  const [quoteId, setQuoteId] = React.useState<string>();

  // init or currency change
  React.useEffect(() => {
    setFields([]);
    setQuoteId(undefined);
  }, [props.values.currencyCode]);

  const previousFieldValues = usePrevious(props.values.fieldValues);
  useUpdated(() => {
    if (!getShouldReload(fields, previousFieldValues, props.values.fieldValues)) return;

    const load = async () => {
      setIsLoading(true);

      try {
        const data = await props.getDynamicFields({
          recipientId: props.beneficiaryId,
          currencyCode: props.values.currencyCode!,
          details: fields.length
            ? getBankDetails(
                {
                  ...props.values.fieldValues,
                  currencyCode: props.values.currencyCode
                },
                fields.filter((x) => x.refreshRequirementsOnChange)
              )
            : undefined,
          quoteId,
          type: props.values.fieldValues?.formType
        });

        setFields(data.fields);
        setQuoteId(data.quoteId);

        if (!props.values.fieldValues) {
          const values: GenericMap<any> = data.fields.reduce((res, x) => ({ ...res, [x.name]: x.value }), {});
          if (props.values.currencyCode === "ZAR") {
            // TODO: better solution
            const formTypeField = data.fields.find((x) => x.name === "formType");
            if (formTypeField?.options?.length) {
              values.formType = formTypeField.options[0].value;
            }
          }
          props.onChange("fieldValues", values);
        }
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [props.beneficiaryId, props.values.fieldValues, props.values.currencyCode, quoteId, fields, previousFieldValues]);

  React.useImperativeHandle(
    props.ref,
    () => ({
      getValues: (data: AddUpdateRecipientFormData): Promise<Account.View.BeneficiarySettings> => {
        const bankName = (() => {
          const fieldKey = "swiftCode";
          const field = fields.find((x) => x.name === fieldKey);
          if (field && field.options?.length) {
            const value = data.fieldValues![fieldKey];
            const caption = field.options!.find((x) => x.value === value)!.caption;

            return caption.split("(")[0].trim();
          }

          return null;
        })();

        const details = getBankDetails(data.fieldValues, fields);
        return Promise.resolve({
          bankName,
          data: {
            wise: {
              additionalData: {
                accountType: data.accountType,
                branchCode: data.branchCode
              },
              details,
              type: data.fieldValues?.formType
            }
          },
          currencyCode: data.currencyCode,
          name: details.accountHolderName
        });
      }
    }),
    [fields]
  );

  const isAccountTypeVisible = React.useCallback(
    () => !fields.some((x) => x.name === "accountType") && props.values.currencyCode?.toLowerCase() === "zar",
    [fields, props.values.currencyCode]
  );

  const isBranchCodeVisible = React.useCallback(
    () => !fields.some((x) => x.name === "branchCode") && props.values.currencyCode?.toLowerCase() === "zar",
    [fields, props.values.currencyCode]
  );

  return (
    <>
      {mapFormElements(i18n, fields, props.values.fieldValues ?? {}, {
        additionalProps: (name: string) => {
          const props: Partial<FormFieldProps<any, any>> = {
            isRequired: true
          };
          if (name === "formType") {
            props.selectBehaviour = "preselectIfSingle";
          }
          return props;
        },
        getComponentProps: (field: DynamicField) => ({
          onChangeTrigger: field.refreshRequirementsOnChange && isFreeInput(field.formControlKey) ? 500 : 0
        }),
        onChange: (name: keyof AddUpdateRecipientFormData, value) =>
          props.onChange("fieldValues", {
            ...props.values.fieldValues,
            [name]: value
          })
      })}

      {!isLoading && isAccountTypeVisible() && (
        <FormField
          Component={Select}
          componentProps={{ options: getAccountTypeOptions(i18n) }}
          isRequired
          label={i18n.accountType}
          name="accountType"
          value={props.values.accountType}
          qa="Account type"
        />
      )}

      {!isLoading && isBranchCodeVisible() && (
        <FormField
          Component={TextInput}
          isRequired
          label={i18n.branchCode}
          name="branchCode"
          value={props.values.branchCode}
          qa="Branch code"
        />
      )}

      {isLoading && <InlinePreloader />}
    </>
  );
};
